import type { KycFileCertificationChoiceDto } from '@dealstack/shared/lib/generated';
import { uploadFileV2 } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	addProofOfPIV,
	deleteProofOfPIV,
	getProofsOfPIV,
	setMorePivDocumentsStatus,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useFileUploadErrorHandler } from '@dealstack/frontend/investor/hooks/useFileUploadErrorHandler';
import { KycDocumentStatus } from '@dealstack/shared/lib/common/business/kyc';
import { AcceptanceStatus, GetInvestmentDetailsRes } from '@dealstack/shared/lib/generated';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	noSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useEffect, useMemo, useState } from 'react';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	investmentDetails: GetInvestmentDetailsRes | undefined;
	kycWorkflowId: string;
}

export const useInvestmentManagePIVProofController = ({
	setIsContinueDisabled,
	investmentDetails,
	kycWorkflowId,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const handleUploadError = useFileUploadErrorHandler();
	const getInvestorDataRequest = useInvestor();

	const [isNewFileUploaded, setIsNewFileUploaded] = useState(false);
	const moreDocsSettings = getInvestorDataRequest.data?.processSettings;

	const pivProofsQuery = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_KYC_INVESTMENT_VEHICLE_GET, kycWorkflowId],
		queryFn: () => getProofsOfPIV(kycWorkflowId),
	});
	const uploadFileMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const addProofMutation = useMutation({
		mutationFn: (variables: Parameters<typeof addProofOfPIV>[0]) => addProofOfPIV(variables),
	});
	const deleteProofMutation = useMutation({
		mutationFn: (variables: Parameters<typeof deleteProofOfPIV>[0]) =>
			deleteProofOfPIV(variables),
	});
	const setMorePivDocumentsStatusMutation = useMutation({
		mutationFn: setMorePivDocumentsStatus,
	});

	const onFileDrop = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadFileMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				addProofMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							const onSuccess = () => {
								showToastV2({
									title: tInvestor('investment.toast.proof_uploaded'),
								});
								setIsNewFileUploaded(true);
								void pivProofsQuery.refetch();
							};

							if (!moreDocsSettings?.areMorePivDocsRequested) {
								onSuccess();
								return;
							}

							setMorePivDocumentsStatusMutation.mutate(kycWorkflowId, {
								onSuccess,
							});
						},
					}
				);
			},
		});
	};

	const proofFilesDetails = useMemo(
		() =>
			pivProofsQuery.data?.payload?.filter(
				({ status }) => status !== AcceptanceStatus.APPROVED
			),
		[pivProofsQuery.data?.payload]
	);

	const onDelete = (proofId: string) => () => {
		const proofToDelete = proofFilesDetails?.find((proof) => proof.id === proofId);
		if (!proofToDelete) {
			return;
		}

		deleteProofMutation.mutate(
			{ fileId: proofToDelete.id, kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void pivProofsQuery.refetch();
				},
			}
		);
	};

	useEffect(() => {
		setIsContinueDisabled(
			!proofFilesDetails?.length ||
				proofFilesDetails.some((file) => isKycRejected(file.status)) ||
				(!isNewFileUploaded &&
					investmentDetails?.pivStatus === KycDocumentStatus.MORE_DOCUMENTS_REQUESTED)
		);
	}, [proofFilesDetails]);

	useWizardStep(noSave);

	return {
		refetchProofs: () => void pivProofsQuery.refetch(),
		uploadFileRequest: uploadFileMutation,
		proofFilesDetails,
		onDelete,
		moreDocsSettings,
		isNewFileUploaded,
		onFileDrop,
	};
};
